import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/aria-role */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState, useMemo, createRef, useCallback } from "react";
import PropTypes from "prop-types";
import Alert, { AlertState } from "../../../../elements/Alert";
import { VerticalSpacing } from "../../../../elements";
import { VerticalSpacingSizes } from "../../../../core/types";
import getClassNameFactory from "../../../../utilities/classNames";
import ChevronDownIcon from "@dcl/icons/esm/ui/chevron-down";
import classNames from "classnames";

var useOutsideClick = function useOutsideClick(ref, callback) {
  var handleMouseDown = function handleMouseDown(e) {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  useEffect(function () {
    document.addEventListener("mousedown", handleMouseDown);
    return function () {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [callback]);
};

var SelectControl = /*#__PURE__*/React.forwardRef(function (props) {
  var defaultValue = props.defaultValue,
      disabled = props.disabled,
      id = props.id,
      labelText = props.labelText,
      renderLabel = props.renderLabel,
      name = props.name,
      onChange = props.onChange,
      options = props.options,
      placeholderText = props.placeholderText,
      required = props.required,
      value = props.value,
      ariaRequired = props.ariaRequired,
      isError = props.isError,
      errorMessage = props.errorMessage,
      _props$optionalText = props.optionalText,
      optionalText = _props$optionalText === void 0 ? "(Optional)" : _props$optionalText,
      instructionalText = props.instructionalText;
  var DEFAULT_INDEX = 0;
  var UNSELECTED_OPTION = -1;
  var selectRef = useRef(null);
  var extendedOptions = options;

  var _useState = useState(function () {
    var defaultValueOption = extendedOptions.find(function (option) {
      return option.value === defaultValue;
    });
    var selectedOption = value ? extendedOptions.find(function (option) {
      return option.value === value;
    }) : defaultValueOption;
    return selectedOption ? selectedOption.label : placeholderText;
  }),
      _useState2 = _slicedToArray(_useState, 2),
      selectValue = _useState2[0],
      setSelectValue = _useState2[1];

  var _useState3 = useState(function () {
    var selectedValue = value || defaultValue;
    var optionIndex = selectedValue ? extendedOptions.findIndex(function (option) {
      return option.value === selectedValue;
    }) : UNSELECTED_OPTION;
    return optionIndex >= DEFAULT_INDEX ? optionIndex : UNSELECTED_OPTION;
  }),
      _useState4 = _slicedToArray(_useState3, 2),
      currentOptionSelected = _useState4[0],
      setSelectedOption = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      dropdownOpened = _useState6[0],
      setOpened = _useState6[1];

  var _useState7 = useState(""),
      _useState8 = _slicedToArray(_useState7, 2),
      errorMessageText = _useState8[0],
      setErrorMessage = _useState8[1];

  var optionsRefsByValue = useMemo(function () {
    var refs = {};
    extendedOptions.forEach(function (_, optionId) {
      refs[optionId] = /*#__PURE__*/createRef(null);
    });
    return refs;
  }, [extendedOptions]);
  var getClassName = getClassNameFactory(SelectControl.displayName);

  var toggleDropdown = function toggleDropdown() {
    if (!disabled) {
      setOpened(!dropdownOpened);
    }
  };

  var setSelectedValue = useCallback(function (optionValue) {
    // Find the index of the selected option
    var index = extendedOptions.findIndex(function (option) {
      return option.label === optionValue;
    }); // Check if the optionValue exists in the extendedOptions array

    if (index === UNSELECTED_OPTION) {
      return;
    }

    setSelectValue(optionValue);
  }, [extendedOptions]);

  var handleOnChange = function handleOnChange(option) {
    if (onChange) {
      onChange(option);
    } // Check if the selected value is empty


    if (required && option.value === "") {
      setErrorMessage(errorMessage || "This field is required.");
    } else {
      setErrorMessage("");
    }

    setSelectedValue(option.label);
  };

  var onSelectClick = function onSelectClick(option) {
    return function (e) {
      e.stopPropagation();
      handleOnChange(option);
      setOpened(false);
    };
  };

  useOutsideClick(selectRef, function () {
    if (dropdownOpened) {
      setOpened(false);
    }
  });
  var onPressEnter = useCallback(function () {
    if (document.activeElement === selectRef.current || dropdownOpened) {
      // Check if an option is selected
      var selectedOption = extendedOptions[currentOptionSelected];

      if (currentOptionSelected !== UNSELECTED_OPTION && dropdownOpened) {
        if (onChange) {
          onChange(selectedOption);
        }

        var isValueEmpty = required && selectedOption.value === "";
        setErrorMessage(isValueEmpty ? errorMessage || "This field is required." : ""); // Update the selectValue state with the label of the selected option

        setSelectValue(selectedOption.label);
      }

      setOpened(!dropdownOpened);
    }
  }, [dropdownOpened, currentOptionSelected, extendedOptions]);
  var onEscaprePress = useCallback(function () {
    if (dropdownOpened) {
      setOpened(false);
    }
  }, [dropdownOpened]);
  var onArrowKeyClick = useCallback(function (direction, e) {
    if (dropdownOpened) {
      e.preventDefault(); // prevent the default behavior of the arrow keys

      var nextOptionIndex = currentOptionSelected;

      if (direction === "down" && nextOptionIndex < extendedOptions.length) {
        nextOptionIndex++;
      } else if (direction === "up" && nextOptionIndex > UNSELECTED_OPTION) {
        nextOptionIndex--;
      }

      var nextOption = extendedOptions[nextOptionIndex];

      while (nextOption && nextOption.disabled) {
        // Skip disabled options
        nextOptionIndex = direction === "down" ? nextOptionIndex + 1 : nextOptionIndex - 1;
        nextOption = extendedOptions[nextOptionIndex];
      }

      if (nextOption) {
        optionsRefsByValue[nextOptionIndex].current.focus();
        setSelectedOption(nextOptionIndex);
        setSelectValue(nextOption.label); // Set the selected value to the one that is focused
      }
    }
  }, [currentOptionSelected, dropdownOpened, extendedOptions, optionsRefsByValue]);

  var onTabClick = function onTabClick(e) {
    if (dropdownOpened) {
      e.preventDefault();
    }
  };

  useEffect(function () {
    var keyHandlers = {
      Enter: onPressEnter,
      Escape: onEscaprePress,
      ArrowDown: function ArrowDown(e) {
        return onArrowKeyClick("down", e);
      },
      ArrowUp: function ArrowUp(e) {
        return onArrowKeyClick("up", e);
      },
      Tab: onTabClick
    };

    var listener = function listener(e) {
      var handler = keyHandlers[e.key];

      if (handler) {
        handler(e);
      }
    };

    document.addEventListener("keydown", listener);
    return function () {
      document.removeEventListener("keydown", listener);
    };
  }, [dropdownOpened, onArrowKeyClick]);
  return /*#__PURE__*/React.createElement("div", {
    className: getClassName()
  }, /*#__PURE__*/React.createElement("label", {
    className: getClassName({
      descendantName: "label",
      states: classNames({
        disabled: disabled
      })
    }),
    htmlFor: id
  }, renderLabel ? renderLabel() : /*#__PURE__*/React.createElement("span", {
    className: getClassName({
      descendantName: "labelText",
      states: classNames({
        disabled: disabled
      })
    })
  }, labelText, !required && !ariaRequired && /*#__PURE__*/React.createElement("span", {
    className: getClassName({
      descendantName: "optional",
      states: classNames({
        disabled: disabled
      })
    })
  }, optionalText), instructionalText && /*#__PURE__*/React.createElement("span", {
    className: getClassName({
      descendantName: "instructional-text",
      states: classNames({
        disabled: disabled
      })
    })
  }, instructionalText)), /*#__PURE__*/React.createElement("span", {
    className: getClassName({
      descendantName: "input",
      states: classNames({
        error: isError
      })
    })
  }, /*#__PURE__*/React.createElement("div", {
    id: id,
    className: getClassName({
      descendantName: "custom-select",
      states: classNames({
        disabled: disabled,
        invalid: errorMessageText
      })
    }),
    onClick: toggleDropdown,
    ref: selectRef,
    role: "control",
    tabIndex: DEFAULT_INDEX,
    name: name,
    "aria-disabled": disabled,
    "aria-expanded": dropdownOpened,
    "aria-invalid": errorMessageText ? "true" : "false"
  }, /*#__PURE__*/React.createElement("button", {
    className: getClassName({
      descendantName: "select-button"
    }),
    tabIndex: UNSELECTED_OPTION
  }, selectValue || placeholderText), /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      descendantName: "arrow"
    })
  }, /*#__PURE__*/React.createElement(ChevronDownIcon, {
    width: 24
  })), dropdownOpened && /*#__PURE__*/React.createElement("ul", {
    className: getClassName({
      descendantName: "select-dropdown"
    }),
    role: "listbox"
  }, extendedOptions.map(function (option, optionId) {
    return /*#__PURE__*/React.createElement("li", {
      tabIndex: DEFAULT_INDEX,
      value: optionId,
      key: option.value,
      "aria-selected": selectValue === option.label,
      onClick: onSelectClick(option),
      role: "option",
      "aria-posinset": optionId + 1,
      ref: optionsRefsByValue[optionId],
      className: getClassName({
        descendantName: "dropdown-item",
        states: option.disabled ? "disabled" : ""
      })
    }, option.label);
  }))))), errorMessageText && /*#__PURE__*/React.createElement(VerticalSpacing, {
    size: VerticalSpacingSizes.XS
  }, /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      descendantName: "error"
    })
  }, /*#__PURE__*/React.createElement(Alert, {
    alertState: AlertState.FAILURE
  }, errorMessageText))));
});
/* eslint-disable max-len */

SelectControl.propTypes = {
  /**
   * Initialize <select> with a value
   */
  defaultValue: PropTypes.string,

  /**
   * Disable the <select>
   */
  disabled: PropTypes.bool,

  /**
   * Id attribute for <select> (and <label> `for` atrribute)
   */
  id: PropTypes.string.isRequired,

  /**
   * Text for <label>
   */
  labelText: PropTypes.string,

  /**
   * Name attribute for <select>, required for serialisation
   */
  name: PropTypes.string.isRequired,

  /**
   * Listen for blur on the <select>.
   */
  onBlur: PropTypes.func,

  /**
   * Listen for changes on the <select>
   *
   * N.B. onChange should only be used for storing the value. Any action related to the value of the <select> should be handled onBlur. See https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-onchange.md
   */
  onChange: PropTypes.func,

  /**
   * Array of objects with `value` and `label` properties
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    segment: PropTypes.string,
    subsegment: PropTypes.string,
    disabled: PropTypes.bool
  })),

  /**
   * Show a placeholder <option>
   */
  placeholder: PropTypes.bool,

  /**
   * Text for placeholder <option>
   */
  placeholderText: PropTypes.string,

  /**
   * Set <select> to be required
   */
  required: PropTypes.bool,

  /**
   * Explicitly set the value of the <select>. Will switch to controlled mode
   */
  value: PropTypes.string,

  /**
   * Provided reference for <select> element
   */
  ref: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),

  /**
   * An alternative to set <select> to be required
   */
  ariaRequired: PropTypes.bool,

  /**
   * Indicates validity of the value
   */
  ariaInvalid: PropTypes.bool,

  /**
   * render custom label
   */
  renderLabel: PropTypes.func,

  /**
   * render custom optionalText
   */
  optionalText: PropTypes.string,
  isError: PropTypes.bool,
  errorMessage: PropTypes.string,
  instructionalText: PropTypes.string
};
/* eslint-enable max-len */

SelectControl.defaultProps = {
  placeholder: true,
  placeholderText: "Select an option…",
  ariaRequired: false,
  ariaInvalid: false,
  isError: false
};
SelectControl.displayName = "Form.SelectControl";
export default SelectControl;